import * as React from 'react';
import { Drawer as MuiDrawer, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Inbox as InboxIcon, Mail as MailIcon } from '@mui/icons-material';
import { Flex } from '@radix-ui/themes';
import { cn } from '@/lib/utils';

// Basically a stand-in replacement for Shadcn drawer (based on Vaul)
// The difference is that this component doesn't cause the 'scroll bar
// and page winking problem'.
//
// Ref forwarding isn't implemented, but would be trivial.

type Direction = "left" | "right" | "top" | "bottom";

interface DrawerProps {
  children: React.ReactNode;
  direction: Direction;
  open?: boolean;
  onOpenChange?: (val: boolean) => void;
}

interface DrawerContentProps {
  children: React.ReactNode;
  className?: string;
}

interface DrawerTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
}

const DrawerContext = React.createContext<{ open: boolean, setOpen: (open: boolean) => void, direction: Direction }>({
  open: false,
  setOpen: () => { },
  direction: "left",
});

const Drawer = ({ children, open: controlledOpen, onOpenChange, direction }: DrawerProps) => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (controlledOpen !== undefined) {
      setOpen(controlledOpen);
    }
  }, [controlledOpen]);

  const handleSetOpen = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setOpen(value);
    }
  };

  return (
    <DrawerContext.Provider value={{ open, setOpen: handleSetOpen, direction }}>
      {children}
    </DrawerContext.Provider>
  );
};

const useDrawer = () => React.useContext(DrawerContext);

const DrawerTrigger = ({ children, asChild = false }: DrawerTriggerProps) => {
  const { setOpen } = useDrawer();

  const triggerProps = {
    onClick: () => setOpen(true)
  };

  return asChild ? React.cloneElement(children, triggerProps) : (
    <div {...triggerProps}>
      {children}
    </div>
  );
};

const DrawerClose = ({ children }: { children: React.ReactNode }) => {
  const { setOpen } = useDrawer();
  return (
    <div onClick={() => setOpen(false)}>
      {children}
    </div>
  );
};

const DrawerContent = ({ children, className }: DrawerContentProps) => {
  const { open, setOpen, direction } = useDrawer();

  return (
    <MuiDrawer anchor={direction} open={open} onClose={() => setOpen(false)} >
      <Flex
        direction="column"
        className={cn("h-full", className)}>
        {children}
      </Flex>
    </MuiDrawer>
  );
};

const DrawerList = ({ items }: { items: string[] }) => (
  <Box sx={{ width: 250 }} role="presentation">
    <List>
      {items.map((text, index) => (
        <ListItem key={text} disablePadding>
          <ListItemButton>
            <ListItemIcon>
              {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
            </ListItemIcon>
            <ListItemText primary={text} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  </Box>
);

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerList
};
